/**
 * Pure numeric shaping for the assistant light's two animated states — the fix from a second
 * real headset session that reported the light as static (on or off, never alive), the session
 * after the one that got the light rendering and coloured at all in the first place
 * (plan/ai-in-room.md, "Fix 2"). Kept separate from `SceneRoot`'s own Babylon plumbing (the
 * light, the scene's render loop) the same way `assistant/PlaybackAmplitude.ts`'s pure
 * `rmsOfByteSamples` is kept separate from its own AudioContext/AnalyserNode plumbing: a plain
 * function of numbers is what this project's test suite can actually exercise directly.
 * `SceneRoot.tickAssistantLight` is the real-headset half, covered by inspection and a
 * desktop-tab run rather than by a unit test — the same status CLAUDE.md already gives every
 * class in this project that owns a real `AudioContext`.
 */

/**
 * 'listening': a slow, continuous rise and fall for as long as the channel is open. A pure
 * function of elapsed time, not of any external signal — nothing about an open microphone
 * says how "loud" the room is, and the owner asked for a breath, not a meter.
 *
 * Shaped with `cos` rather than `sin` so the wave starts *at* `base` when `elapsedMs` is 0 —
 * the instant a turn opens — and eases upward from there, rather than starting already
 * mid-swing. Never below `base`: a caller reading this the instant the state changes still
 * sees a lit, non-zero light rather than one waiting for the wave to rise.
 */
export function breathingIntensity(elapsedMs: number, base: number, swing: number, periodMs: number): number {
  const phase = (elapsedMs / periodMs) * Math.PI * 2;
  return base + swing * (0.5 - 0.5 * Math.cos(phase));
}

/**
 * 'speaking': tracks the reply clip's own playback amplitude — see
 * `AssistantController.onAmplitudeChanged`, fed by a `PlaybackAmplitude` analyser over the
 * `<audio>` element actually playing the clip. `amplitude` is expected as an RMS value in
 * [0, 1], but clamped here defensively rather than trusted: it arrives from real playback
 * plumbing this function has no visibility into. A silent instant still reads as `base`, the
 * same reasoning `breathingIntensity` uses for `elapsedMs = 0` — the light should dim with a
 * quiet moment in the clip, never vanish while a turn is still technically speaking.
 */
export function speakingIntensity(amplitude: number, base: number, gain: number): number {
  const clamped = Math.min(1, Math.max(0, amplitude));
  return base + clamped * gain;
}
